// Core
import { NgModule } from '@angular/core';
import { environment } from 'src/environments/environment';

// Modules
import { BrowserModule } from '@angular/platform-browser';
import { CommonModule } from '@angular/common';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';

import { DragDropModule } from '@angular/cdk/drag-drop';
import { FormsModule } from '@angular/forms';
import { ReactiveFormsModule } from '@angular/forms';

import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatListModule } from '@angular/material/list';
import { MatMenuModule } from '@angular/material/menu';
import { MatNativeDateModule } from '@angular/material/core';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { MatTableModule } from '@angular/material/table';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatTabsModule } from '@angular/material/tabs';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatBadgeModule } from '@angular/material/badge';
import { MatBottomSheetModule } from '@angular/material/bottom-sheet';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatChipsModule } from '@angular/material/chips';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatSliderModule } from '@angular/material/slider';
import { MatStepperModule } from '@angular/material/stepper';
import { MatDividerModule } from '@angular/material/divider';
import { MatTreeModule } from '@angular/material/tree';
import { MatRadioModule } from '@angular/material/radio';

import { NgxSliderModule } from '@angular-slider/ngx-slider';

import { AppRoutingModule } from './app-routing.module';
import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';
import { IPublicClientApplication, PublicClientApplication, InteractionType, BrowserCacheLocation, LogLevel } from '@azure/msal-browser';
import { MsalGuard, MsalInterceptor, MsalBroadcastService, MsalInterceptorConfiguration, MsalModule, MsalService, MSAL_GUARD_CONFIG, MSAL_INSTANCE, MSAL_INTERCEPTOR_CONFIG, MsalGuardConfiguration, MsalRedirectComponent } from '@azure/msal-angular';

// Services
import { BaseApiService } from './services/api-base.service';
import { EventService } from './services/event.service';
import { HelperService } from './services/helper.service';

// Components
import { AppComponent } from './app.component';
import { FailedComponent } from './pages/failed/failed.component';
import { HomeComponent } from './home/home.component';
import { ProfileComponent } from './pages/profile/profile.component';
import { UserDetailsComponent } from './components/user-details/user-details.component';
import { RosterSidenavComponent } from './components/roster-sidenav/roster-sidenav.component';
import { MiniMenuComponent } from './components/roster-sidenav/mini-menu/mini-menu.component';

import { PublishComponent } from './components/publish/publish.component';
import { RosterComponent } from './components/roster/roster.component';
import { CustomLightboxComponent } from './components/custom-lightbox/custom-lightbox.component';
import { CarePlanDialogComponent } from './components/care-plan-dialog/care-plan-dialog.component';
import { ConfirmationDialogComponent } from './components/confirmation-dialog/confirmation-dialog.component';
import { ProgressDialogComponent } from './components/progress-dialog/progress-dialog.component';
import { PublishProgressDialogComponent } from './components/publish/publish-progress-dialog.component';
import { ReallocateDialogComponent } from './components/reallocate-dialog/reallocate-dialog.component';
import { FilterGridDialogComponent } from './components/filter-grid-dialog/filter-grid-dialog.component';
import { EmailMismatchDialogComponent } from './components/email-mismatch-dialog/email-mismatch-dialog.component';
import { NotificationDialogComponent } from './components/notification-dialog/notification-dialog.component';
import { RosterUploadDialogComponent } from './components/roster-upload-dialog/roster-upload-dialog.component';
import { WorklogEditorComponent } from './components/worklog-editor/worklog-editor.component';
import { WorklogSidenavComponent } from './components/worklog-sidenav/worklog-sidenav.component';
import { WorklogDeleteConfirmationDialogComponent } from './components/worklog-delete-confirmation-dialog/worklog-delete-confirmation-dialog.component';
import { WorklogDeletionProgressDialogComponent } from './components/worklog-deletion-progress-dialog/worklog-deletion-progress-dialog.component';
import { WorklogChartDeactivationDialogComponent } from './components/worklog-chart-deactivation-dialog/worklog-chart-deactivation-dialog.component';
import { AdhocServiceComponent } from './components/adhoc-service/adhoc-service.component';
import { AdhocServiceSidenavComponent } from './components/adhoc-service-sidenav/adhoc-service-sidenav.component';
import { ClientSelectDialogComponent } from './components/client-select-dialog/client-select-dialog.component';
import { StaffSelectDialogComponent } from './components/staff-select-dialog/staff-select-dialog.component';
import { FundingSelectDialogComponent } from './components/funding-select-dialog/funding-select-dialog.component';
import { ActivitySelectDialogComponent } from './components/activity-select-dialog/activity-select-dialog.component';

// MSAL Configuration
export function MSALInstanceFactory(): IPublicClientApplication {
  return new PublicClientApplication({
    auth: {
      clientId: environment.msalConfig.auth.clientId,
      authority: environment.msalConfig.auth.authority,
      redirectUri: environment.msalConfig.auth.redirectUri,
      postLogoutRedirectUri: environment.msalConfig.auth.postLogoutRedirectUri
    },
    cache: {
      cacheLocation: BrowserCacheLocation.LocalStorage
    },
    system: {
      allowNativeBroker: false, // Disables WAM Broker
      loggerOptions: {
        logLevel: LogLevel.Info,
        piiLoggingEnabled: false
      }
    }
  });
}

export function MSALInterceptorConfigFactory(): MsalInterceptorConfiguration {
  const protectedResourceMap = new Map<string, string[]>();
  protectedResourceMap.set(environment.backend.uri, []);
  protectedResourceMap.set(environment.graphApi.uri, environment.graphApi.scopes);

  return {
    interactionType: InteractionType.Redirect,
    protectedResourceMap
  };
}

export function MSALGuardConfigFactory(): MsalGuardConfiguration {
  return { 
    interactionType: InteractionType.Redirect,
    authRequest: {
      scopes: [...environment.graphApi.scopes]
    },
    loginFailedRoute: '/login-failed'
  };
}

@NgModule({
  declarations: [
    AppComponent,
    HomeComponent,
    ProfileComponent,
    FailedComponent,
    RosterComponent,
    PublishComponent,
    CustomLightboxComponent,
    CarePlanDialogComponent,
    ConfirmationDialogComponent,
    ProgressDialogComponent,
    PublishProgressDialogComponent,
    ReallocateDialogComponent,
    UserDetailsComponent,
    RosterSidenavComponent,
    MiniMenuComponent,
    EmailMismatchDialogComponent,
    FilterGridDialogComponent,
    NotificationDialogComponent,
    RosterUploadDialogComponent,
    WorklogEditorComponent,
    WorklogSidenavComponent,
    WorklogDeleteConfirmationDialogComponent,
    WorklogDeletionProgressDialogComponent,
    WorklogChartDeactivationDialogComponent,
    AdhocServiceComponent,
    AdhocServiceSidenavComponent,
    ClientSelectDialogComponent,
    StaffSelectDialogComponent,
    FundingSelectDialogComponent,
    ActivitySelectDialogComponent,
  ],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    CommonModule,

    DragDropModule,
    FormsModule,
    ReactiveFormsModule,

    MatAutocompleteModule,
    MatButtonModule,
    MatCardModule,
    MatCheckboxModule,
    MatDatepickerModule,
    MatDialogModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatListModule,
    MatMenuModule,
    MatNativeDateModule,
    MatProgressSpinnerModule,
    MatSelectModule,
    MatSlideToggleModule,
    MatSnackBarModule,
    MatTableModule,
    MatToolbarModule,
    MatTabsModule,
    MatTooltipModule,
    MatBadgeModule,
    MatBottomSheetModule,
    MatButtonToggleModule,
    MatChipsModule,
    MatExpansionModule,
    MatProgressBarModule,
    MatSidenavModule,
    MatSliderModule,
    MatStepperModule,
    MatDividerModule,
    MatTreeModule,
    MatRadioModule,

    NgxSliderModule,

    AppRoutingModule,
    HttpClientModule,
    MsalModule,
  ],
  providers: [
    BaseApiService,
    EventService,
    HelperService,
    {
      provide: HTTP_INTERCEPTORS,
      useClass: MsalInterceptor,
      multi: true
    },
    {
      provide: MSAL_INSTANCE,
      useFactory: MSALInstanceFactory
    },
    {
      provide: MSAL_GUARD_CONFIG,
      useFactory: MSALGuardConfigFactory
    },
    {
      provide: MSAL_INTERCEPTOR_CONFIG,
      useFactory: MSALInterceptorConfigFactory
    },
    MsalService,
    MsalGuard,
    MsalBroadcastService
  ],
  bootstrap: [AppComponent, MsalRedirectComponent]
})
export class AppModule { }
